import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Database, Package, Users, ShieldCheck, ArrowRight, Flame, Wrench, ShieldWarning, Folders, Sparkle, Lightbulb, Lightning } from "@phosphor-icons/react";
import api, { checkDbStatus } from "@/lib/api";
import { canAccessCustomers } from "@/lib/access";
import { useT } from "@/lib/i18n";
import { useBranding } from "@/lib/branding";
import BrandLogo from "@/components/BrandLogo";
import { APP_VERSION } from "@/lib/version";
import WavingFlag from "@/components/WavingFlag";
import AdminPanel from "@/components/AdminPanel";
import AccountMenu from "@/components/AccountMenu";
import PushBell from "@/components/PushBell";
import PwaInstallBanner from "@/components/PwaInstallBanner";
import MyBriefingsCard from "@/components/MyBriefingsCard";
import RecentChangesBadge from "@/components/RecentChangesBadge";
import SuggestChangeButton from "@/components/SuggestChangeButton";
import TopBarToolsMenu from "@/components/TopBarToolsMenu";

export default function LandingPage({ user, onLogout, onLogoutAll }) {
  const [dbConnected, setDbConnected] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [serviceTechCan, setServiceTechCan] = useState({ can: false, as: null });
  const [pendingSuggestions, setPendingSuggestions] = useState(0);
  // iter150j — programmatically open the Suggest-a-change modal from
  // the Tools dropdown without rendering its own pill in the topbar.
  const [suggestOpenSignal, setSuggestOpenSignal] = useState(0);
  const navigate = useNavigate();
  const t = useT();
  const branding = useBranding();
  const customersEnabled = canAccessCustomers(user);

  useEffect(() => {
    checkDbStatus().then((d) => setDbConnected(d.connected)).catch(() => setDbConnected(false));
  }, []);

  // Probe Service-Tech visibility (staff or compressor-owners only).
  useEffect(() => {
    let cancelled = false;
    api.get("/me/can-service-tech")
      .then((r) => { if (!cancelled) setServiceTechCan(r.data || { can: false }); })
      .catch(() => { if (!cancelled) setServiceTechCan({ can: false }); });
    return () => { cancelled = true; };
  }, []);

  // iter149d — pull pending-suggestions count so the "Requested Changes"
  // landing button can show how many user-submitted improvements are
  // waiting in triage. Silent on 401/403 (non-admin users just see no
  // count badge, which is correct).
  useEffect(() => {
    if (!user?.is_admin && !user?.is_super_admin) return;
    let cancelled = false;
    api.get("/admin/suggestions?status=pending")
      .then((r) => { if (!cancelled) setPendingSuggestions(r.data?.counts?.pending || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.is_admin, user?.is_super_admin]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-2 sm:px-6 h-14 flex items-center justify-between gap-1">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-shrink">
            <BrandLogo size={22} className="text-blue-700 shrink-0" />
            <div className="flex flex-col min-w-0">
              <h1 className="text-sm sm:text-lg font-black tracking-tighter font-['Chivo'] truncate min-w-0 leading-tight">
                <span className="sm:hidden">{branding.short_name || t("brand.name", "Compressors R Us")}</span>
                <span className="hidden sm:inline">{branding.title || t("brand.name", "Compressors R Us")}</span>
              </h1>
              <span data-testid="topbar-version"
                    title={`App version ${APP_VERSION} — bumped each iteration so you can tell preview from production at a glance.`}
                    className="text-[9px] font-mono font-bold tracking-wider text-slate-500 cursor-help leading-none">
                {APP_VERSION}
              </span>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 ml-1 sm:ml-3 shrink-0">
              <div className="relative flex items-center justify-center w-3 h-3">
                {dbConnected === true && (
                  <><span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-50 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" /></>
                )}
                {dbConnected === false && <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />}
                {dbConnected === null && <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-300 animate-pulse" />}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {user?.is_admin && <PushBell />}
            <RecentChangesBadge />
            <TopBarToolsMenu
              user={user}
              pendingSuggestions={pendingSuggestions}
              onOpenAdmin={() => setShowAdmin(true)}
              onOpenSuggest={() => setSuggestOpenSignal((n) => n + 1)}
            />
            {/* Headless instance — opens via openSignal from Tools menu */}
            <SuggestChangeButton hideTrigger openSignal={suggestOpenSignal} />
            <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
          </div>
        </div>
      </header>

      {/* Landing Content */}
      <main className="max-w-[1200px] mx-auto px-3 sm:px-6 py-8 sm:py-16">
        <div className="text-center mb-6">
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter font-['Chivo'] text-slate-900">
            {t("landing.headline", "What would you like to view?")}
          </h2>
          <p className="text-base text-slate-500 mt-3">{t("landing.subheadline", "Select a section to get started")}</p>
        </div>

        {/* Admin tooling sub-link row — placed above the main cards so admins
            can jump to ops pages without scrolling past the dashboard cards. */}
        {user?.is_admin && (
          <div className="max-w-[800px] mx-auto mb-8 flex gap-2 justify-center flex-wrap" data-testid="landing-admin-pills">
            <button
              data-testid="landing-service-visits-dashboard"
              onClick={() => navigate("/admin/service-visits")}
              className="text-[11px] font-bold uppercase tracking-wider text-violet-700 hover:text-violet-900 inline-flex items-center gap-1 px-2 py-1 border border-violet-100 rounded hover:bg-violet-50"
            >
              {t("landing.admin_visit_dashboard", "Visit dashboard")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-service-techs-leaderboard"
              onClick={() => navigate("/admin/service-techs")}
              className="text-[11px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900 inline-flex items-center gap-1 px-2 py-1 border border-amber-100 rounded hover:bg-amber-50"
            >
              {t("landing.admin_tech_leaderboard", "Tech leaderboard")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-positions"
              onClick={() => navigate("/admin/positions")}
              className="text-[11px] font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 px-2 py-1 border border-blue-100 rounded hover:bg-blue-50"
            >
              {t("landing.admin_positions", "Positions")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-warranty-admin"
              onClick={() => navigate("/admin/warranty")}
              className="text-[11px] font-bold uppercase tracking-wider text-rose-700 hover:text-rose-900 inline-flex items-center gap-1 px-2 py-1 border border-rose-100 rounded hover:bg-rose-50"
            >
              {t("landing.admin_warranty_claims", "Warranty claims")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-warranty-insights"
              onClick={() => navigate("/admin/warranty/insights")}
              className="text-[11px] font-bold uppercase tracking-wider text-violet-700 hover:text-violet-900 inline-flex items-center gap-1 px-2 py-1 border border-violet-100 rounded hover:bg-violet-50"
            >
              {t("landing.admin_insights", "Insights")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-warranty-master-doc"
              onClick={() => navigate("/admin/warranty/master-doc")}
              className="text-[11px] font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 px-2 py-1 border border-blue-100 rounded hover:bg-blue-50"
            >
              {t("landing.admin_master_doc", "Master Doc")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-commissioning-reviews"
              onClick={() => navigate("/admin/commissioning/reviews")}
              className="text-[11px] font-bold uppercase tracking-wider text-orange-700 hover:text-orange-900 inline-flex items-center gap-1 px-2 py-1 border border-orange-100 rounded hover:bg-orange-50"
            >
              {t("landing.admin_eng_reviews", "Eng Reviews")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-automations"
              onClick={() => navigate("/admin/automations")}
              className="text-[11px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900 inline-flex items-center gap-1 px-2 py-1 border border-amber-100 rounded hover:bg-amber-50"
            >
              {t("landing.admin_automations", "Automations")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-customer-aux"
              onClick={() => navigate("/admin/customer-aux")}
              className="text-[11px] font-bold uppercase tracking-wider text-violet-700 hover:text-violet-900 inline-flex items-center gap-1 px-2 py-1 border border-violet-100 rounded hover:bg-violet-50"
            >
              {t("landing.admin_discounts", "Discounts")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-discount-approvals"
              onClick={() => navigate("/admin/discount-approvals")}
              className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 px-2 py-1 border border-emerald-100 rounded hover:bg-emerald-50"
            >
              {t("landing.admin_discount_approvals", "Discount approvals")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-scorecard"
              onClick={() => navigate("/admin/commissioning/scorecard")}
              className="text-[11px] font-bold uppercase tracking-wider text-rose-700 hover:text-rose-900 inline-flex items-center gap-1 px-2 py-1 border border-rose-100 rounded hover:bg-rose-50"
            >
              {t("landing.admin_dealer_scorecard", "Dealer Scorecard")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-dealer-agreements"
              onClick={() => navigate("/admin/dealer-agreements")}
              className="text-[11px] font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 px-2 py-1 border border-blue-100 rounded hover:bg-blue-50"
            >
              {t("landing.admin_dealer_agreements", "Dealer Agreements")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-drive-oauth"
              onClick={() => navigate("/admin/drive-oauth")}
              className="text-[11px] font-bold uppercase tracking-wider text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1 border border-slate-200 rounded hover:bg-slate-50"
            >
              {t("landing.admin_drive_oauth", "Drive OAuth")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-email-limits"
              onClick={() => navigate("/admin/email-limits")}
              className="text-[11px] font-bold uppercase tracking-wider text-fuchsia-700 hover:text-fuchsia-900 inline-flex items-center gap-1 px-2 py-1 border border-fuchsia-100 rounded hover:bg-fuchsia-50"
            >
              {t("landing.admin_email_limits", "Email Limits")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-improvement-log"
              onClick={() => navigate("/admin/improvement-log")}
              className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1 px-2 py-1 border border-indigo-100 rounded hover:bg-indigo-50"
            >
              {t("landing.admin_improvement_log", "Improvement Log")} <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-requested-changes"
              onClick={() => navigate("/admin/requested-changes")}
              className="text-[11px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900 inline-flex items-center gap-1 px-2 py-1 border border-amber-100 rounded hover:bg-amber-50"
            >
              {t("landing.admin_requested_changes", "Requested Changes")}
              {pendingSuggestions > 0 && (
                <span data-testid="landing-requested-changes-count"
                      className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-amber-500 text-white text-[9px] font-black">
                  {pendingSuggestions}
                </span>
              )}
              <ArrowRight size={11} weight="bold" />
            </button>
            <button
              data-testid="landing-evidence-rules"
              onClick={() => navigate("/admin/warranty/evidence-rules")}
              className="text-[11px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900 inline-flex items-center gap-1 px-2 py-1 border border-amber-100 rounded hover:bg-amber-50"
            >
              {t("landing.admin_evidence_rules", "Evidence Rules")} <ArrowRight size={11} weight="bold" />
            </button>
            {(user?.is_test_admin || user?.is_super_admin) && (
              <button
                data-testid="landing-test-queue"
                onClick={() => navigate("/admin/test-queue")}
                className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 px-2 py-1 border border-emerald-100 rounded hover:bg-emerald-50"
              >
                {t("landing.admin_test_queue", "Test Queue")} <ArrowRight size={11} weight="bold" />
              </button>
            )}
            {(user?.is_hr_admin || user?.is_super_admin) && (
              <button
                data-testid="landing-hr-admin"
                onClick={() => navigate("/admin/hr")}
                className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1 px-2 py-1 border border-indigo-100 rounded hover:bg-indigo-50"
              >
                {t("landing.admin_hr", "HR Workspace")} <ArrowRight size={11} weight="bold" />
              </button>
            )}
            {(user?.is_admin || user?.is_super_admin) && (
              <button
                data-testid="landing-admin-manuals"
                onClick={() => navigate("/admin/manuals")}
                className="text-[11px] font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 px-2 py-1 border border-blue-100 rounded hover:bg-blue-50"
              >
                {t("landing.admin_manuals", "Manuals editor")} <ArrowRight size={11} weight="bold" />
              </button>
            )}
            <button
              data-testid="landing-test-company"
              onClick={() => navigate("/admin/test-company")}
              className="text-[11px] font-bold uppercase tracking-wider text-amber-800 hover:text-amber-950 inline-flex items-center gap-1 px-2 py-1 border border-amber-200 rounded hover:bg-amber-50"
            >
              {t("landing.admin_test_company", "Test Company")} <ArrowRight size={11} weight="bold" />
            </button>
          </div>
        )}

        <div className={`grid ${customersEnabled ? "md:grid-cols-2" : "md:grid-cols-1"} gap-8 max-w-[800px] mx-auto`}>
          {/* Price Lists Card */}
          <button
            data-testid="landing-price-lists"
            onClick={() => navigate("/us-class")}
            className="group relative bg-white border-2 border-slate-200 rounded-sm p-8 text-left hover:border-blue-400 hover:shadow-lg transition-all duration-200"
          >
            <div className="flex items-start justify-between mb-6">
              <div className="w-12 h-12 rounded-sm bg-blue-50 flex items-center justify-center">
                <Package size={28} weight="bold" className="text-blue-700" />
              </div>
              <ArrowRight size={20} weight="bold" className="text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
            </div>
            <h3 className="text-xl font-bold font-['Chivo'] text-slate-900 mb-2">{t("landing.price_lists_title", "Final Products Price Lists")}</h3>
            <p className="text-sm text-slate-500 mb-4">
              {t("landing.price_lists_desc", "Browse US and Canadian compressor price lists with filters, sorting, and export options.")}
            </p>
            <div className="flex items-center gap-3">
              <WavingFlag src="https://flagcdn.com/w320/us.png" width={36} height={22} speed={0.6} />
              <WavingFlag src="https://flagcdn.com/w320/ca.png" width={36} height={22} speed={0.6} />
            </div>
          </button>

          {/* Customers Card (only for allowed domains) */}
          {customersEnabled && (
            <button
              data-testid="landing-customers"
              onClick={() => navigate("/customers-us")}
              className="group relative bg-white border-2 border-slate-200 rounded-sm p-8 text-left hover:border-blue-400 hover:shadow-lg transition-all duration-200"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-12 h-12 rounded-sm bg-blue-50 flex items-center justify-center">
                  <Users size={28} weight="bold" className="text-blue-700" />
                </div>
                <ArrowRight size={20} weight="bold" className="text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
              </div>
              <h3 className="text-xl font-bold font-['Chivo'] text-slate-900 mb-2">{t("landing.customers_title", "Customers")}</h3>
              <p className="text-sm text-slate-500 mb-4">
                {t("landing.customers_desc", "View customer lists grouped by country — USA, Canada, and other regions.")}
              </p>
              <div className="flex items-center gap-3">
                <WavingFlag src="https://flagcdn.com/w320/us.png" width={36} height={22} speed={0.6} />
                <WavingFlag src="https://flagcdn.com/w320/ca.png" width={36} height={22} speed={0.6} />
                <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-1 rounded-sm">{t("landing.others_chip", "+ Others")}</span>
              </div>
            </button>
          )}
        </div>

        {/* Products Sold (Fleet) — available to admins and to customer-tagged users */}
        {customersEnabled && (
          <div className="max-w-[800px] mx-auto mt-8">
            <button
              data-testid="landing-products-sold"
              onClick={() => navigate("/compressors")}
              className="group relative w-full bg-white border-2 border-slate-200 rounded-sm p-6 text-left hover:border-blue-400 hover:shadow-lg transition-all duration-200 flex items-center gap-5"
            >
              <div className="w-12 h-12 rounded-sm bg-blue-50 flex items-center justify-center shrink-0">
                <Package size={28} weight="bold" className="text-blue-700" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold font-['Chivo'] text-slate-900">{t("landing.products_sold_title", "Products Sold")}</h3>
                <p className="text-sm text-slate-500">
                  {t("landing.products_sold_desc", "Fleet view by serial number — grouped into US Air Compressor and Air Compressor Canada folders, with service-hour alerts.")}
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-2 shrink-0">
                <img src="https://usaircompressor.com/wp-content/uploads/2024/09/logo_reduced.webp" alt="US Air" className="h-6 w-auto" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                <img src="https://aircompressorcanada.com/wp-content/uploads/2026/04/acc_black.png" alt="ACC" className="h-6 w-auto" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              </div>
              <ArrowRight size={20} weight="bold" className="text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all shrink-0" />
            </button>
          </div>
        )}

        {/* iter122 — Conversations v2 (unified Company-level chat) */}
        <div className="max-w-[800px] mx-auto mt-4">
          <button
            data-testid="landing-conversations-v2"
            onClick={() => navigate("/conversations-v2")}
            className="group relative w-full bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-sm p-6 text-left hover:border-blue-400 hover:shadow-lg transition-all duration-200 flex items-center gap-5"
          >
            <div className="w-12 h-12 rounded-sm bg-blue-600 flex items-center justify-center shrink-0">
              <Users size={26} weight="fill" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <h3 className="text-lg font-bold font-['Chivo'] text-slate-900">{t("landing.conversations_title", "Conversations")}</h3>
                <span className="text-[9px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 border border-blue-300 px-1.5 py-0.5 rounded-sm">
                  {t("landing.badge_new", "New")}
                </span>
              </div>
              <p className="text-sm text-slate-600">
                {t("landing.conversations_desc", "Unified Company-level threads — chat with any descendant or ancestor in your tree, with auto-generated titles and full message history.")}
              </p>
            </div>
            <ArrowRight size={20} weight="bold" className="text-blue-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all shrink-0" />
          </button>
        </div>

        {/* iter150AE — Sequencing System (Phase 1A) */}
        <div className="max-w-[800px] mx-auto mt-4">
          <button
            data-testid="landing-sequencing"
            onClick={() => navigate("/sequencing")}
            className="group relative w-full bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-sm p-6 text-left hover:border-amber-400 hover:shadow-lg transition-all duration-200 flex items-center gap-5"
          >
            <div className="w-12 h-12 rounded-sm bg-amber-600 flex items-center justify-center shrink-0">
              <Lightning size={26} weight="fill" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <h3 className="text-lg font-bold font-['Chivo'] text-slate-900">
                  {t("landing.sequencing_title", "Sequencing")}
                </h3>
                <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-sm">
                  {t("landing.badge_new", "New")}
                </span>
              </div>
              <p className="text-sm text-slate-600">
                {t("landing.sequencing_desc", "Multi-compressor coordination, type catalog, disclaimers, holidays — and live-control commissioning.")}
              </p>
            </div>
            <ArrowRight size={20} weight="bold" className="text-amber-300 group-hover:text-amber-700 group-hover:translate-x-1 transition-all shrink-0" />
          </button>
        </div>

        {/* Service Tech — visible to staff + customer-side users who own a compressor */}
        {serviceTechCan.can && (
          <div className="max-w-[800px] mx-auto mt-4">
            <button
              data-testid="landing-service-tech"
              onClick={() => navigate("/service-tech")}
              className="group relative w-full bg-gradient-to-br from-violet-50 to-blue-50 border-2 border-violet-200 rounded-sm p-6 text-left hover:border-violet-400 hover:shadow-lg transition-all duration-200 flex items-center gap-5"
            >
              <div className="w-12 h-12 rounded-sm bg-violet-600 flex items-center justify-center shrink-0">
                <Wrench size={26} weight="fill" className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <h3 className="text-lg font-bold font-['Chivo'] text-slate-900">{t("service_tech.title")}</h3>
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 border border-violet-300 px-1.5 py-0.5 rounded-sm">
                    {serviceTechCan.as === "staff" ? t("landing.staff_badge", "Staff") : t("landing.owner_badge", "Owner")}
                  </span>
                </div>
                <p className="text-sm text-slate-600">
                  {t("service_tech.description")}
                </p>
              </div>
              <ArrowRight size={20} weight="bold" className="text-violet-300 group-hover:text-violet-600 group-hover:translate-x-1 transition-all shrink-0" />
            </button>
          </div>
        )}

        {/* Commissioning — visible to staff + customer-side users who own a compressor */}
        {serviceTechCan.can && (
          <div className="max-w-[800px] mx-auto mt-3 space-y-2">
            <button
              data-testid="landing-commissioning"
              onClick={() => navigate("/commissioning")}
              className="group relative w-full bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-sm p-6 text-left hover:border-emerald-400 hover:shadow-lg transition-all duration-200 flex items-center gap-5"
            >
              <div className="w-12 h-12 rounded-sm bg-emerald-600 flex items-center justify-center shrink-0">
                <Wrench size={26} weight="fill" className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <h3 className="text-lg font-bold font-['Chivo'] text-slate-900">{t("commissioning.title")}</h3>
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-300 px-1.5 py-0.5 rounded-sm">
                    {serviceTechCan.as === "staff" ? t("landing.staff_badge", "Staff") : t("landing.owner_badge", "Owner")}
                  </span>
                </div>
                <p className="text-sm text-slate-600">
                  {t("commissioning.description")}
                </p>
              </div>
              <ArrowRight size={20} weight="bold" className="text-emerald-300 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all shrink-0" />
            </button>
            <div className="flex justify-end">
              <button
                data-testid="landing-commissioning-history"
                onClick={() => navigate("/commissioning/history")}
                className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 px-1"
              >
                {t("landing.view_history", "View history")} <ArrowRight size={11} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {/* Claim Warranty — visible to everyone who can see Service Tech (i.e. owns a compressor or is staff) */}
        {serviceTechCan.can && (
          <div className="max-w-[800px] mx-auto mt-4">
            <button
              data-testid="landing-warranty-claim"
              onClick={() => navigate("/warranty/claim")}
              className="group relative w-full bg-gradient-to-br from-rose-50 to-amber-50 border-2 border-rose-200 rounded-sm p-6 text-left hover:border-rose-400 hover:shadow-lg transition-all duration-200 flex items-center gap-5"
            >
              <div className="w-12 h-12 rounded-sm bg-rose-600 flex items-center justify-center shrink-0">
                <ShieldWarning size={26} weight="fill" className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <h3 className="text-lg font-bold font-['Chivo'] text-slate-900">{t("landing.warranty_claim_title", "Claim Warranty")}</h3>
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 border border-rose-300 px-1.5 py-0.5 rounded-sm">
                    {t("landing.warranty_claim_ai_badge", "AI-assisted")}
                  </span>
                </div>
                <p className="text-sm text-slate-600">
                  {t("landing.warranty_claim_desc", "File a warranty claim with photos, voltage readings, an installation video, and a recorded technical explanation. We auto-route to the ACC service department with a ticket number.")}
                </p>
              </div>
              <ArrowRight size={20} weight="bold" className="text-rose-300 group-hover:text-rose-600 group-hover:translate-x-1 transition-all shrink-0" />
            </button>
          </div>
        )}

        {/* Pre-call briefing widget — staff only. Auto-hides when there are no rows. */}
        {(user?.is_admin || user?.role === "staff") && (
          <div className="max-w-[800px] mx-auto mt-4">
            <MyBriefingsCard />
          </div>
        )}
      </main>

      {showAdmin && <AdminPanel user={user} onClose={() => setShowAdmin(false)} />}
      <PwaInstallBanner />
    </div>
  );
}
